import {
  AppSessionCellId,
  CellId,
  ExploreSpec,
  mergeExploreSpecWithSessionState,
} from "@hex/common";
import type { ExploreSpecSessionStateUpdateableFields } from "@hex/common";
import { useCallback } from "react";
import { shallowEqual } from "../../util/index.js";

import { useStableRef } from "../../hooks/useStableRef.js";
import { useSelector, useStore } from "../../redux/hooks.js";
import { appSessionMPSelectors } from "../../redux/slices/appSessionMPSlice.js";
import { CellContentsMP } from "../../redux/slices/hexVersionMPSlice.js";
import { useProjectContext } from "../../util/projectContext.js";
import { useSessionContext } from "../../util/sessionContext.js";

// Nested WeakMap cache for memoizing session state merging results
// Outer key: base ExploreSpec, Inner key: session state, Value: merged ExploreSpec result
const mergeCache = new WeakMap<
  ExploreSpec,
  WeakMap<ExploreSpecSessionStateUpdateableFields, ExploreSpec>
>();

/**
 * Cached wrapper around mergeExploreSpecWithSessionState for rendering performance.
 * Uses nested WeakMaps for automatic garbage collection and reference-based caching.
 * Since merging producses a new fields array/spec each time, we can cache the result to prevent unnecessary re-renders.
 *
 * @param spec - The base ExploreSpec
 * @param sessionState - The session state with field overrides
 * @returns The merged ExploreSpec with session state overrides applied
 */
export function cachedMergeExploreSpecWithSessionState(
  spec: ExploreSpec,
  sessionState: ExploreSpecSessionStateUpdateableFields | null,
): ExploreSpec {
  if (!sessionState) {
    return spec;
  }

  // Get or create the inner map for this spec
  let innerMap = mergeCache.get(spec);
  if (!innerMap) {
    innerMap = new WeakMap();
    mergeCache.set(spec, innerMap);
  }

  // Check if we have a cached result for this sessionState
  const cached = innerMap.get(sessionState);
  if (cached) {
    return cached;
  }

  // Compute and cache the result
  const result = mergeExploreSpecWithSessionState(spec, sessionState);
  innerMap.set(sessionState, result);
  return result;
}

type ExploreCellContents = CellContentsMP & { __typename: "ExploreCell" };

type SafeExploreCellContents<S extends boolean> = S extends false
  ? ExploreCellContents
  : ExploreCellContents | undefined;

export type UseExploreCellContentsSelectorArgs<T> =
  | {
      cellId: CellId;
      appSessionCellId?: AppSessionCellId;
      selector: (cellContents: ExploreCellContents) => T;
      equalityFn?: (left: T, right: T) => boolean;
      safe?: false;
    }
  | {
      cellId: CellId | undefined;
      appSessionCellId?: AppSessionCellId;
      selector: (cellContents: ExploreCellContents | undefined) => T;
      equalityFn?: (left: T, right: T) => boolean;
      safe: true;
    };

/**
 * Selects the explore cell contents for a given cell ID, with automatic session state merging for published apps.
 *
 * @param args.cellId - The cell to select the contents from
 * @param args.appSessionCellId - Optional app session cell ID (auto-detected for published apps if not provided)
 * @param args.selector - Optional selector function to extract specific data from the cell contents
 * @param args.equalityFn - Optional equality function to compare the selected data (default: shallowEqual)
 * @param args.safe - Whether to handle missing cells gracefully (default: false)
 */
export function useExploreCellContentsSelector<T>(
  args: UseExploreCellContentsSelectorArgs<T>,
): T {
  const { appSessionId, isLogicSession } = useSessionContext();
  const { hexVersionId } =
    useProjectContext({ safe: args.safe ?? false }) ?? {};

  return useSelector((state) => {
    const selectors =
      appSessionMPSelectors.getExploreCellContentsSelectors(appSessionId);

    if (args.safe) {
      const contents = selectors.safeSelectWithSessionState(
        state,
        args.cellId,
        hexVersionId,
        args.appSessionCellId,
        isLogicSession,
      );
      return args.selector(contents);
    } else {
      if (hexVersionId == null) {
        throw new Error(
          "hexVersionId is required for explore cell contents selector",
        );
      }
      const contents = selectors.selectWithSessionState(
        state,
        args.cellId,
        hexVersionId,
        args.appSessionCellId,
        isLogicSession,
      );
      return args.selector(contents);
    }
  }, args.equalityFn ?? shallowEqual);
}

/**
 * Selects the explore spec for a given cell ID, with automatic session state merging for published apps.
 *
 * In published apps, session state (user customizations like sorts/filters) is automatically merged
 * with the base spec.
 *
 * @param args.cellId - The cell to select the spec from
 * @param args.appSessionCellId - Optional app session cell ID (auto-detected for published apps if not provided)
 * @param args.selector - Optional selector function to extract specific data from the spec (default: returns the spec)
 * @param args.equalityFn - Optional equality function to compare the selected data (default: shallowEqual)
 */
export function useExploreSpecSelector<T = ExploreSpec>(args: {
  cellId: CellId;
  appSessionCellId?: AppSessionCellId;
  selector?: (spec: ExploreSpec) => T;
  equalityFn?: (left: T, right: T) => boolean;
}): T {
  return useExploreCellContentsSelector({
    cellId: args.cellId,
    appSessionCellId: args.appSessionCellId,
    selector: (contents) => {
      return args.selector
        ? args.selector(contents.spec)
        : (contents.spec as T);
    },
    equalityFn: args.equalityFn,
  });
}

/**
 * Safe version of useExploreSpecSelector that handles missing cells or non-explore cells
 *
 * @param args.cellId - The cell to select the spec from (can be undefined)
 * @param args.appSessionCellId - Optional app session cell ID (auto-detected for published apps if not provided)
 * @param args.selector - Optional selector function to extract specific data from the spec (default: returns the spec)
 * @param args.equalityFn - Optional equality function to compare the selected data (default: shallowEqual)
 */
export function useSafeExploreSpecSelector<T = ExploreSpec | undefined>(args: {
  cellId: CellId | undefined;
  appSessionCellId?: AppSessionCellId;
  selector?: (spec: ExploreSpec | undefined) => T;
  equalityFn?: (left: T, right: T) => boolean;
}): T {
  return useExploreCellContentsSelector({
    cellId: args.cellId,
    appSessionCellId: args.appSessionCellId,
    selector: (contents) => {
      return args.selector
        ? args.selector(contents?.spec)
        : (contents?.spec as T);
    },
    equalityFn: args.equalityFn,
    safe: true,
  });
}

export type UseExploreCellContentsGetterArgs<
  ARGS extends unknown[],
  SAFE extends boolean,
  T,
> = {
  selector?: (cellContents: SafeExploreCellContents<SAFE>, ...args: ARGS) => T;
  safe?: SAFE;
};

export type UseExploreCellContentsGetterResult<
  ARGS extends unknown[],
  SAFE extends boolean,
  T,
> = (
  cellId: SAFE extends false ? CellId : CellId | undefined,
  appSessionCellId?: AppSessionCellId,
  ...args: ARGS
) => T;

/**
 * Returns a getter function for explore cell contents with automatic session state merging.
 *
 * Similar to useCellContentsGetter but includes session state merging for published apps.
 *
 * @param args.selector - Optional selector to transform the cell contents
 * @param args.safe - Whether to handle missing cells gracefully (default: false)
 * @returns A function that takes (cellId, appSessionCellId?, ...args) and returns the selected data
 */
export function useExploreCellContentsGetter<
  ARGS extends unknown[] = [],
  SAFE extends boolean = false,
  T = SafeExploreCellContents<SAFE>,
>(
  args: UseExploreCellContentsGetterArgs<ARGS, SAFE, T> = {},
): UseExploreCellContentsGetterResult<ARGS, SAFE, T> {
  const { appSessionId, isLogicSession } = useSessionContext();
  const { hexVersionId } =
    useProjectContext({ safe: args.safe ?? false }) ?? {};
  const store = useStore();
  const selectorRef = useStableRef(args.selector);

  return useCallback(
    (
      cellId: SAFE extends false ? CellId : CellId | undefined,
      appSessionCellId?: AppSessionCellId,
      ...selectorArgs: ARGS
    ): T => {
      if (cellId == null) {
        if (args.safe) {
          const currentSelector = selectorRef.current;
          return currentSelector
            ? currentSelector(
                undefined as SafeExploreCellContents<SAFE>,
                ...selectorArgs,
              )
            : (undefined as T);
        } else {
          throw new Error(
            `cellId is required for useExploreCellContentsGetter`,
          );
        }
      }
      const contents = appSessionMPSelectors
        .getExploreCellContentsSelectors(appSessionId)
        .safeSelectWithSessionState(
          store.getState(),
          cellId,
          hexVersionId,
          appSessionCellId,
          isLogicSession,
        );

      if (contents == null && !args.safe) {
        throw new Error(`Missing explore cell contents for cell: ${cellId}`);
      }

      const currentSelector = selectorRef.current;
      return currentSelector
        ? currentSelector(
            contents as SafeExploreCellContents<SAFE>,
            ...selectorArgs,
          )
        : (contents as T);
    },
    [appSessionId, hexVersionId, store, selectorRef, args.safe, isLogicSession],
  );
}

export function useExploreSpecGetter<T = ExploreSpec>(
  selector?: (spec: ExploreSpec) => T,
): (cellId: CellId, appSessionCellId?: AppSessionCellId) => T {
  return useExploreCellContentsGetter({
    selector: selector
      ? (contents) => selector(contents.spec)
      : (contents) => contents.spec as T,
  });
}
